// /src/pages/DailySummaryPage.tsx
// VERSIÓN DEFINITIVA: Se corrige el parsing de la fecha para evitar "Invalid Date".

import React, { useEffect, useState, useMemo, useCallback } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../App';

// --- Material-UI Imports ---
import {
    Container, Box, Button, Paper, TextField, Typography, Select, MenuItem,
    FormControl, InputLabel, Stack, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, CircularProgress, Snackbar, Alert, Chip, Autocomplete,
    IconButton, Divider
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import SearchIcon from '@mui/icons-material/Search';
import SummarizeIcon from '@mui/icons-material/Summarize';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DeleteIcon from '@mui/icons-material/Delete';
import ClearAllIcon from '@mui/icons-material/ClearAll';


// --- TIPOS E INTERFACES ---
interface ReportRow {
    date: string;
    total_clicks: number;
    total_conversions: number;
    lp_clicks: number;
    total_cost: string;
    approved_payout: string;
    profit: string;
    roi: string;
    lp_ctr: string;
    cpc: string;
    epc: string;
    cvr: string;
}

interface ReportTotals {
    total_clicks: number;
    total_conversions: number;
    lp_clicks: number;
    total_cost: number;
    approved_payout: number;
    profit: number;
    roi: number;
    lp_ctr: number;
    cpc: number;
    epc: number;
    cvr: number;
}

interface FilterOption { id: number; name: string; }

interface ActiveFilter {
    id: number;
    dimension: 'campaign' | 'offer' | 'trafficSource' | 'cpaNetwork' | 'landingPage' | '';
    value: number | '';
}

type DateRangeOption = 'today' | 'yesterday' | 'last_7_days' | 'last_30_days' | 'this_month' | 'last_month' | 'all_time' | 'custom';

// --- LÓGICA DE FECHAS CON ZONA HORARIA ---
const TIMEZONE = 'America/Mexico_City';

const formatDateToISO = (date: Date): string => {
    return date.toISOString().split('T')[0];
};

const getDateRange = (range: DateRangeOption): { startDate: string, endDate: string } => {
    const now = new Date();
    const parts = new Intl.DateTimeFormat('en-CA', { timeZone: TIMEZONE }).formatToParts(now);
    const year = parts.find(p => p.type === 'year')?.value;
    const month = parts.find(p => p.type === 'month')?.value;
    const day = parts.find(p => p.type === 'day')?.value;

    if (!year || !month || !day) {
        console.error("No se pudieron obtener las partes de la fecha para la zona horaria.");
        const fallbackDate = new Date();
        fallbackDate.setHours(0, 0, 0, 0);
        return { startDate: formatDateToISO(fallbackDate), endDate: formatDateToISO(fallbackDate) };
    }

    const todayInZoneAsUTC = new Date(Date.UTC(Number(year), Number(month) - 1, Number(day)));

    let start = new Date(todayInZoneAsUTC);
    let end = new Date(todayInZoneAsUTC);

    switch (range) {
        case 'today':
            break;
        case 'yesterday':
            start.setUTCDate(todayInZoneAsUTC.getUTCDate() - 1);
            end.setUTCDate(todayInZoneAsUTC.getUTCDate() - 1);
            break;
        case 'last_7_days':
            start.setUTCDate(todayInZoneAsUTC.getUTCDate() - 6);
            break;
        case 'last_30_days':
            start.setUTCDate(todayInZoneAsUTC.getUTCDate() - 29);
            break;
        case 'this_month':
            start = new Date(Date.UTC(todayInZoneAsUTC.getUTCFullYear(), todayInZoneAsUTC.getUTCMonth(), 1));
            end = new Date(todayInZoneAsUTC);
            break;
        case 'last_month':
            end = new Date(Date.UTC(todayInZoneAsUTC.getUTCFullYear(), todayInZoneAsUTC.getUTCMonth(), 0));
            start = new Date(Date.UTC(end.getUTCFullYear(), end.getUTCMonth(), 1));
            break;
        case 'all_time':
            start = new Date('2020-01-01T00:00:00.000Z');
            end = new Date(todayInZoneAsUTC);
            break;
    }
    return { startDate: formatDateToISO(start), endDate: formatDateToISO(end) };
};


// --- HELPER COMPONENTS ---
const DailySummaryTable: React.FC<{
    reportData: ReportRow[];
    isLoading: boolean;
    error: string | null;
}> = ({ reportData, isLoading, error }) => {
    const theme = useTheme();
    const headerCellStyle = { fontWeight: 'bold', backgroundColor: theme.palette.action.hover };

    if (isLoading && reportData.length === 0) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
                <Typography sx={{ ml: 2 }}>Cargando reporte diario...</Typography>
            </Box>
        );
    }

    if (error) {
        return ( <Alert severity="error" sx={{ m: 2 }}>Error: {error}</Alert> );
    }

    if (reportData.length === 0 && !isLoading) {
        return ( <Typography sx={{ textAlign: 'center', p: 4 }} color="text.secondary">No se encontraron datos para la selección actual.</Typography> );
    }

    return (
        <Paper elevation={3} sx={{ overflow: 'hidden', borderRadius: '12px', mt: 4 }}>
            <TableContainer>
                <Table stickyHeader size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={headerCellStyle}>Fecha</TableCell>
                            <TableCell sx={headerCellStyle} align="right">Clics</TableCell>
                            <TableCell sx={headerCellStyle} align="right">Conv.</TableCell>
                            <TableCell sx={headerCellStyle} align="right">LP CTR</TableCell>
                            <TableCell sx={headerCellStyle} align="right">Costo</TableCell>
                            <TableCell sx={headerCellStyle} align="right">Payout</TableCell>
                            <TableCell sx={headerCellStyle} align="right">Beneficio</TableCell>
                            <TableCell sx={headerCellStyle} align="right">ROI</TableCell>
                            <TableCell sx={headerCellStyle} align="right">CPC</TableCell>
                            <TableCell sx={headerCellStyle} align="right">EPC</TableCell>
                            <TableCell sx={headerCellStyle} align="right">CVR</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {reportData.map((row, index) => (
                            <TableRow key={row.date || index} hover>
                                {/* CORRECCIÓN FINAL: Se reemplazan guiones por diagonales para compatibilidad universal del navegador */}
                                <TableCell>{new Date(row.date.replace(/-/g, '/')).toLocaleDateString('es-MX')}</TableCell>
                                <TableCell align="right">{row.total_clicks.toLocaleString()}</TableCell>
                                <TableCell align="right">{row.total_conversions.toLocaleString()}</TableCell>
                                <TableCell align="right"><Chip label={`${parseFloat(row.lp_ctr).toFixed(2)}%`} size="small" /></TableCell>
                                <TableCell align="right"><Chip label={`$${parseFloat(row.total_cost).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`} size="small" /></TableCell>
                                <TableCell align="right"><Chip label={`$${parseFloat(row.approved_payout).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`} size="small" color="success" /></TableCell>
                                <TableCell align="right"><Chip label={`$${parseFloat(row.profit).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`} size="small" color={parseFloat(row.profit) >= 0 ? 'success' : 'error'}/></TableCell>
                                <TableCell align="right"><Chip label={`${parseFloat(row.roi).toFixed(2)}%`} size="small" color={parseFloat(row.roi) >= 0 ? 'success' : 'error'} /></TableCell>
                                <TableCell align="right"><Chip label={`$${parseFloat(row.cpc).toFixed(2)}`} size="small" /></TableCell>
                                <TableCell align="right"><Chip label={`$${parseFloat(row.epc).toFixed(2)}`} size="small" /></TableCell>
                                <TableCell align="right"><Chip label={`${parseFloat(row.cvr).toFixed(2)}%`} size="small" /></TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </Paper>
    );
};


const DailySummaryPage: React.FC = () => {
    const [reportData, setReportData] = useState<ReportRow[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [notification, setNotification] = useState<{ type: 'success' | 'error', message: string } | null>(null);

    const [reportTotals, setReportTotals] = useState<ReportTotals>({
        total_clicks: 0, total_conversions: 0, lp_clicks: 0, total_cost: 0,
        approved_payout: 0, profit: 0, roi: 0, lp_ctr: 0, cpc: 0, epc: 0, cvr: 0,
    });

    const [selectedDateRange, setSelectedDateRange] = useState<DateRangeOption>('this_month');
    const initialDates = getDateRange(selectedDateRange);
    const [startDateFilter, setStartDateFilter] = useState<string>(initialDates.startDate);
    const [endDateFilter, setEndDateFilter] = useState<string>(initialDates.endDate);
    
    const [activeFilters, setActiveFilters] = useState<ActiveFilter[]>([]);

    const [filterOptions, setFilterOptions] = useState<{
        campaigns: FilterOption[], offers: FilterOption[], trafficSources: FilterOption[],
        cpaNetworks: FilterOption[], landingPages: FilterOption[]
    }>({ campaigns: [], offers: [], trafficSources: [], cpaNetworks: [], landingPages: [] });


    const fetchFilterOptions = useCallback(async () => {
        try {
            const [campaignsRes, offersRes, tsRes, cpaRes, lpRes] = await Promise.all([
                axios.get(`${API_BASE_URL}/campaigns`),
                axios.get(`${API_BASE_URL}/offers`),
                axios.get(`${API_BASE_URL}/traffic-sources`),
                axios.get(`${API_BASE_URL}/cpa-networks`),
                axios.get(`${API_BASE_URL}/landing-pages`),
            ]);
            setFilterOptions({
                campaigns: campaignsRes.data.map((item: any) => ({ id: item.id, name: item.name })),
                offers: offersRes.data.map((item: any) => ({ id: item.id, name: item.name })),
                trafficSources: tsRes.data.map((item: any) => ({ id: item.id, name: item.name })),
                cpaNetworks: cpaRes.data.map((item: any) => ({ id: item.id, name: item.name })),
                landingPages: lpRes.data.map((item: any) => ({ id: item.id, name: item.name })),
            });
        } catch (e: any) {
            setNotification({ type: 'error', message: e.response?.data?.message || 'Error al cargar opciones de filtro.' });
            console.error("Error fetching filter options:", e);
        }
    }, []);

    useEffect(() => {
        fetchFilterOptions();
    }, [fetchFilterOptions]);
    
    useEffect(() => {
        const fetchReportData = async () => {
            setIsLoading(true);
            setError(null);
            setReportData([]);
            setReportTotals({
                total_clicks: 0, total_conversions: 0, lp_clicks: 0, total_cost: 0, approved_payout: 0,
                profit: 0, roi: 0, lp_ctr: 0, cpc: 0, epc: 0, cvr: 0,
            });

            try {
                const apiEndDate = new Date(endDateFilter + 'T00:00:00.000Z');
                apiEndDate.setUTCDate(apiEndDate.getUTCDate() + 1);
                
                const dimensions = 'date';
                let url = `${API_BASE_URL}/reports/query?startDate=${startDateFilter}&endDate=${formatDateToISO(apiEndDate)}&dimensions=${dimensions}`;

                activeFilters.forEach(filter => {
                    if (filter.dimension && filter.value) {
                        const apiDimensionMap = {
                            campaign: 'campaign',
                            offer: 'offer',
                            trafficSource: 'traffic_source',
                            cpaNetwork: 'cpa_network',
                            landingPage: 'landing_page'
                        };
                        url += `&${apiDimensionMap[filter.dimension]}=${filter.value}`;
                    }
                });

                const response = await axios.get<ReportRow[]>(url);
                setReportData(response.data);

                const calculatedTotals: ReportTotals = response.data.reduce((acc, row) => {
                    acc.total_clicks += row.total_clicks;
                    acc.total_conversions += row.total_conversions;
                    acc.lp_clicks += row.lp_clicks;
                    acc.total_cost += parseFloat(row.total_cost);
                    acc.approved_payout += parseFloat(row.approved_payout);
                    return acc;
                }, {
                    total_clicks: 0, total_conversions: 0, lp_clicks: 0, total_cost: 0, approved_payout: 0,
                    profit: 0, roi: 0, lp_ctr: 0, cpc: 0, epc: 0, cvr: 0,
                });

                calculatedTotals.profit = calculatedTotals.approved_payout - calculatedTotals.total_cost;
                calculatedTotals.roi = calculatedTotals.total_cost > 0 ? (calculatedTotals.profit / calculatedTotals.total_cost) * 100 : (calculatedTotals.approved_payout > 0 ? Infinity : -100);
                calculatedTotals.lp_ctr = calculatedTotals.total_clicks > 0 ? (calculatedTotals.lp_clicks / calculatedTotals.total_clicks) * 100 : 0;
                calculatedTotals.cpc = calculatedTotals.total_clicks > 0 ? calculatedTotals.total_cost / calculatedTotals.total_clicks : 0;
                calculatedTotals.epc = calculatedTotals.total_clicks > 0 ? calculatedTotals.approved_payout / calculatedTotals.total_clicks : 0;
                calculatedTotals.cvr = calculatedTotals.total_clicks > 0 ? (calculatedTotals.total_conversions / calculatedTotals.total_clicks) * 100 : 0;

                setReportTotals(calculatedTotals);

            } catch (err: any) {
                const errorMessage = err.response?.data?.message || err.message || 'Error desconocido al cargar el reporte.';
                setError(errorMessage);
                setNotification({ type: 'error', message: errorMessage });
            } finally {
                setIsLoading(false);
            }
        };

        fetchReportData();
    }, [startDateFilter, endDateFilter, activeFilters]);

    const handleFilterSubmit = (event: React.FormEvent) => {
        event.preventDefault();
        const currentFilters = [...activeFilters];
        setActiveFilters(currentFilters);
    };
    
    const addFilter = () => {
        setActiveFilters(prev => [...prev, { id: Date.now(), dimension: '', value: '' }]);
    };
    
    const removeFilter = (id: number) => {
        setActiveFilters(prev => prev.filter(f => f.id !== id));
    };

    const updateFilter = (id: number, field: 'dimension' | 'value', value: any) => {
        setActiveFilters(prev => prev.map(f => f.id === id ? { ...f, [field]: value, ...(field === 'dimension' && { value: '' }) } : f));
    };

    const clearAllFilters = () => {
        setSelectedDateRange('this_month');
        const defaultDates = getDateRange('this_month');
        setStartDateFilter(defaultDates.startDate);
        setEndDateFilter(defaultDates.endDate);
        setActiveFilters([]);
    };

    const getFilterNameById = (dimension: string, id: number): string => {
        const key = `${dimension}s` as keyof typeof filterOptions;
        const optionSet = filterOptions[key];
        if (!optionSet) return 'Desconocido';
        const found = optionSet.find((opt: FilterOption) => opt.id === id);
        return found ? found.name : String(id);
    };

    return (
        <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
            <Snackbar open={!!notification} autoHideDuration={4000} onClose={() => setNotification(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                <Alert onClose={() => setNotification(null)} severity={notification?.type} sx={{ width: '100%' }}>{notification?.message}</Alert>
            </Snackbar>

            <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 'bold', color: 'text.primary' }}>
                <SummarizeIcon sx={{ verticalAlign: 'middle', mr: 1, fontSize: '2.5rem' }} />
                Resumen Diario
            </Typography>
            
            <Paper elevation={3} sx={{ p: { xs: 2, md: 3 }, mb: 4, borderRadius: '12px' }}>
                <Stack spacing={2}>
                    <Typography variant="h6" component="h2" color="text.primary">Filtros</Typography>
                    
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
                        <FormControl sx={{ minWidth: 180 }} size="small">
                            <InputLabel>Rango</InputLabel>
                            <Select
                                value={selectedDateRange}
                                onChange={(e) => {
                                    const newRange = e.target.value as DateRangeOption;
                                    setSelectedDateRange(newRange);
                                    if (newRange !== 'custom') {
                                        const { startDate, endDate } = getDateRange(newRange);
                                        setStartDateFilter(startDate);
                                        setEndDateFilter(endDate);
                                    }
                                }}
                                label="Rango"
                            >
                                <MenuItem value="today">Hoy</MenuItem>
                                <MenuItem value="yesterday">Ayer</MenuItem>
                                <MenuItem value="last_7_days">Últimos 7 Días</MenuItem>
                                <MenuItem value="last_30_days">Últimos 30 Días</MenuItem>
                                <MenuItem value="this_month">Este Mes</MenuItem>
                                <MenuItem value="last_month">Mes Anterior</MenuItem>
                                <MenuItem value="all_time">Histórico</MenuItem>
                                <MenuItem value="custom">Personalizado</MenuItem>
                            </Select>
                        </FormControl>
                        <TextField size="small" label="Desde" type="date" value={startDateFilter} onChange={e => { setStartDateFilter(e.target.value); setSelectedDateRange('custom'); }} InputLabelProps={{ shrink: true }}/>
                        <TextField size="small" label="Hasta" type="date" value={endDateFilter} onChange={e => { setEndDateFilter(e.target.value); setSelectedDateRange('custom'); }} InputLabelProps={{ shrink: true }}/>
                    </Stack>
                    
                    <Divider />
                    
                    {activeFilters.map((filter) => {
                        const dimensionMap = {
                            campaign: { options: filterOptions.campaigns, label: 'Campaña' },
                            offer: { options: filterOptions.offers, label: 'Oferta' },
                            trafficSource: { options: filterOptions.trafficSources, label: 'Fuente de Tráfico' },
                            cpaNetwork: { options: filterOptions.cpaNetworks, label: 'Red CPA' },
                            landingPage: { options: filterOptions.landingPages, label: 'Landing Page' },
                        };
                        const currentOptions = filter.dimension ? dimensionMap[filter.dimension].options : [];

                        return (
                            <Stack key={filter.id} direction="row" spacing={1} alignItems="center">
                                <FormControl size="small" sx={{ minWidth: 180 }}>
                                    <InputLabel>Dimensión</InputLabel>
                                    <Select
                                        value={filter.dimension}
                                        label="Dimensión"
                                        onChange={(e) => updateFilter(filter.id, 'dimension', e.target.value)}
                                    >
                                        <MenuItem value="campaign">Campaña</MenuItem>
                                        <MenuItem value="offer">Oferta</MenuItem>
                                        <MenuItem value="trafficSource">Fuente de Tráfico</MenuItem>
                                        <MenuItem value="cpaNetwork">Red CPA</MenuItem>
                                        <MenuItem value="landingPage">Landing Page</MenuItem>
                                    </Select>
                                </FormControl>
                                <Autocomplete
                                    fullWidth
                                    size="small"
                                    options={currentOptions}
                                    getOptionLabel={(option) => `(${option.id}) ${option.name}`}
                                    isOptionEqualToValue={(option, value) => option.id === value.id}
                                    value={currentOptions.find(opt => opt.id === filter.value) || null}
                                    onChange={(event, newValue) => {
                                        updateFilter(filter.id, 'value', newValue ? newValue.id : '');
                                    }}
                                    renderInput={(params) => <TextField {...params} label="Valor" />}
                                    disabled={!filter.dimension}
                                />
                                <IconButton onClick={() => removeFilter(filter.id)} color="error">
                                    <DeleteIcon />
                                </IconButton>
                            </Stack>
                        )
                    })}
                    <Button startIcon={<AddCircleOutlineIcon />} onClick={addFilter} sx={{ alignSelf: 'flex-start' }}>Añadir Filtro</Button>

                    <Divider />

                    <Stack direction="row" spacing={2} justifyContent="flex-end">
                        <Button onClick={clearAllFilters} startIcon={<ClearAllIcon />} disabled={isLoading}>Limpiar Filtros</Button>
                        <Button onClick={handleFilterSubmit} variant="contained" disabled={isLoading} startIcon={isLoading ? <CircularProgress size={20} /> : <SearchIcon />}>
                            Aplicar Filtros
                        </Button>
                    </Stack>
                </Stack>
            </Paper>
            
            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mb: 2 }}>
                {activeFilters.map(filter => {
                    if(!filter.dimension || !filter.value) return null;
                    const dimensionLabel = {
                        campaign: 'Campaña', offer: 'Oferta', trafficSource: 'Fuente de Tráfico',
                        cpaNetwork: 'Red CPA', landingPage: 'Landing Page'
                    }[filter.dimension];
                    const valueLabel = getFilterNameById(filter.dimension, filter.value);

                    return(
                        <Chip
                            key={filter.id}
                            label={`${dimensionLabel}: (${filter.value}) ${valueLabel}`}
                            onDelete={() => removeFilter(filter.id)}
                        />
                    );
                })}
            </Stack>

            <DailySummaryTable reportData={reportData} isLoading={isLoading} error={error} />

            {reportData.length > 0 && !isLoading && !error && (
                <Paper elevation={3} sx={{ overflow: 'hidden', borderRadius: '12px', mt: 4, p: 3 }}>
                    <Typography variant="h6" component="h3" gutterBottom color="text.primary">
                        Totales del Período
                    </Typography>
                    <TableContainer>
                        <Table size="small">
                            <TableBody>
                                <TableRow><TableCell sx={{ fontWeight: 'bold' }}>Clics Totales</TableCell><TableCell align="right">{reportTotals.total_clicks.toLocaleString()}</TableCell><TableCell sx={{ fontWeight: 'bold' }}>Conversiones Totales</TableCell><TableCell align="right">{reportTotals.total_conversions.toLocaleString()}</TableCell></TableRow>
                                <TableRow><TableCell sx={{ fontWeight: 'bold' }}>LP Clicks Totales</TableCell><TableCell align="right">{reportTotals.lp_clicks.toLocaleString()}</TableCell><TableCell sx={{ fontWeight: 'bold' }}>Costo Total</TableCell><TableCell align="right"><Chip label={`$${reportTotals.total_cost.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`} size="small" /></TableCell></TableRow>
                                <TableRow><TableCell sx={{ fontWeight: 'bold' }}>Payout Aprobado Total</TableCell><TableCell align="right"><Chip label={`$${reportTotals.approved_payout.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`} size="small" color="success" /></TableCell><TableCell sx={{ fontWeight: 'bold' }}>Beneficio Total</TableCell><TableCell align="right"><Chip label={`$${reportTotals.profit.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`} size="small" color={reportTotals.profit >= 0 ? 'success' : 'error'}/></TableCell></TableRow>
                                <TableRow><TableCell sx={{ fontWeight: 'bold' }}>ROI Total</TableCell><TableCell align="right"><Chip label={`${reportTotals.roi.toFixed(2)}%`} size="small" color={reportTotals.roi >= 0 ? 'success' : 'error'} /></TableCell><TableCell sx={{ fontWeight: 'bold' }}>LP CTR Total</TableCell><TableCell align="right"><Chip label={`${reportTotals.lp_ctr.toFixed(2)}%`} size="small" /></TableCell></TableRow>
                                <TableRow><TableCell sx={{ fontWeight: 'bold' }}>CPC Promedio</TableCell><TableCell align="right"><Chip label={`$${reportTotals.cpc.toFixed(2)}`} size="small" /></TableCell><TableCell sx={{ fontWeight: 'bold' }}>EPC Promedio</TableCell><TableCell align="right"><Chip label={`$${reportTotals.epc.toFixed(2)}`} size="small" /></TableCell></TableRow>
                                <TableRow><TableCell sx={{ fontWeight: 'bold' }}>CVR Promedio</TableCell><TableCell align="right"><Chip label={`${reportTotals.cvr.toFixed(2)}%`} size="small" /></TableCell><TableCell></TableCell><TableCell></TableCell></TableRow>
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>
            )}
        </Container>
    );
};

export default DailySummaryPage;